/*
 * Cart Tiers progress bar.
 *
 * Renders a single, segmented "spend more to unlock" bar into every
 * [data-cart-tiers] container (the cart drawer today). Milestones come from:
 *   - window.SHFTD_CART_TIERS  — the shftd.cart_tiers shop metafield (entry
 *     multiplier / bonus-entry / display effects), and
 *   - window.SHFTD_FREE_SHIPPING_THRESHOLD — the store's free-ship minimum,
 *     surfaced as an automatic "Free shipping" milestone (so it stays in sync
 *     with Shopify settings — no duplicate config, no drift).
 *
 * Entries-only / display-only: this NEVER changes prices. Free shipping is
 * delivered by Shopify; money-off isn't part of it. The bar is purely a
 * conversion nudge that tracks the live cart subtotal.
 *
 * Live updates mirror cart-entries-preview.js: refresh on cart events + a
 * MutationObserver on #CartDrawer (covers add/remove rebuilds), debounced.
 */

(function () {
  "use strict";

  var FETCH = "/cart.js";
  var milestones = null; // computed once from config
  var reachedKeys = Object.create(null); // for celebrate-on-cross
  var primed = false; // suppress celebrate on the very first paint
  var prevCurAdd = 0; // last rendered cumulative tier add, for the level-up combine
  var isFetching = false;
  var pending = false;
  var lastSubtotal = null; // most recent rendered subtotal (optimistic qty base)
  var reconcileTimer = null;
  var scrollHoldUntil = 0; // freeze the ladder's movement until a level-up finishes
  var scrollDeferTimer = null; // re-applies the held scroll once the hold expires

  function num(v) {
    var n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  function money(cents) {
    var v = Math.max(0, Math.round(cents)) / 100;
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    try {
      var _numLoc = (window.shftdLocale && window.shftdLocale.number_locale) || "en-US";
      return v.toLocaleString(_numLoc, {
        style: "currency",
        currency: cur,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: v % 1 === 0 ? 0 : 2,
      });
    } catch (_e) {
      return "$" + v.toFixed(2);
    }
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // Small inline icons (stroke, currentColor). Keyed to the editor's icon names.
  var ICONS = {
    truck: '<path d="M3 7h11v8H3z"/><path d="M14 10h4l3 3v2h-7z"/><circle cx="7" cy="17" r="1.6"/><circle cx="17" cy="17" r="1.6"/>',
    gift: '<path d="M4 11h16v9H4z"/><path d="M2 7h20v4H2z"/><path d="M12 7v13"/><path d="M12 7S10 3 7.5 4 9.5 7 12 7Zm0 0s2-4 4.5-3-2.5 3-4.5 3Z"/>',
    star: '<path d="m12 3 2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 19.2l1-5.8L3.5 9.2l5.9-.9z"/>',
    bolt: '<path d="M13 2 4 14h6l-1 8 9-12h-6z"/>',
    ticket: '<path d="M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2"/><path d="M15 5v2"/><path d="M15 11v2"/><path d="M15 17v2"/>',
  };
  function iconSvg(name) {
    var p = ICONS[name] || ICONS.bolt;
    return (
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + p + "</svg>"
    );
  }
  // Format a cumulative multiplier face: "2X", "5X", "1.5X".
  function fmtMult(n) {
    var r = Math.round(n * 10) / 10;
    return (r % 1 === 0 ? r.toFixed(0) : r.toFixed(1)) + "X";
  }

  /** Build the sorted milestone ladder from config + the free-ship threshold. */
  function buildMilestones() {
    var out = [];
    var cfg = window.SHFTD_CART_TIERS;
    if (cfg && cfg.enabled && Array.isArray(cfg.tiers)) {
      cfg.tiers.forEach(function (t) {
        var at = Math.round(num(t.minSubtotalCents));
        var multInc = 0, multPctInc = 0, bonus = 0, bonusBase = 0, disp = null, dispIcon = null;
        (Array.isArray(t.effects) ? t.effects : []).forEach(function (e) {
          if (e.type === "entry_multiplier") multInc += num(e.value);
          else if (e.type === "entry_multiplier_pct") multPctInc += num(e.value);
          else if (e.type === "bonus_entries") bonus += num(e.value);
          else if (e.type === "bonus_entries_base") bonusBase += num(e.value);
          else if (e.type === "display" && e.label) { disp = String(e.label); if (e.icon) dispIcon = e.icon; }
        });
        if (multInc > 0 || multPctInc > 0 || bonus > 0 || bonusBase > 0 || disp) {
          out.push({ at: at, multInc: multInc, multPctInc: multPctInc, bonus: bonus, bonusBase: bonusBase, disp: disp, dispIcon: dispIcon, key: "t:" + (t.id || at) });
        }
      });
    }
    // Auto free-shipping milestone from the store threshold.
    var fs = Math.round(num(window.SHFTD_FREE_SHIPPING_THRESHOLD));
    if (fs > 0 && !out.some(function (m) { return m.at === fs && m.disp && /shipping/i.test(m.disp); })) {
      out.push({ at: fs, multInc: 0, multPctInc: 0, bonus: 0, bonusBase: 0, disp: (window.shftdLocale && window.shftdLocale.free_shipping) || "Free shipping", dispIcon: "truck", key: "freeship" });
    }
    out.sort(function (a, b) { return a.at - b.at; });

    // Each rung carries its CUMULATIVE additive tier bonus (tiers ADD, they don't
    // multiply). The displayed face/reward = the cart's base rate + that bonus,
    // resolved at paint time via currentBase() so the rung matches the per-item
    // capsule exactly (e.g. a base-10 Gold Pass + "+2" tier shows 12X on both).
    var addRunning = 0, pctRunning = 0;
    out.forEach(function (m, i) {
      if (m.multInc > 0 || m.multPctInc > 0) {
        addRunning += m.multInc;
        pctRunning += m.multPctInc;
        m.isMult = true;
        m.cumAdd = addRunning; // fixed increments; base added at paint time
        m.cumPct = pctRunning; // base-relative increments; × base at paint time
        m.icon = null;
      } else if (m.bonus > 0 || m.bonusBase > 0) {
        // Bonus rungs: ticket icon in the dot, "+N Entries" label (resolved at
        // paint from base × bonusBase, so it rescales with the store base).
        m.isBonus = true;
        m.icon = "ticket";
      } else {
        m.face = null; // icon-only (e.g. free shipping)
        m.reward = m.disp || "reward";
        m.icon = m.dispIcon || "truck";
      }
      m.rarity = Math.min(i, 4);
      // Rung positions are computed per-paint in render() from the scroll geometry
      // (railGeom), so nothing to bake here beyond rarity.
    });
    return out;
  }

  // The bar always reflects the STORE DEFAULT rate (e.g. 8×/$1) + the tier add,
  // so tier 1 reads 10X (8+2) store-wide. A product with a higher base (e.g. a
  // Gold Pass at 10×) earns more — 12X (10+2) — but that product-specific total
  // belongs on the per-item capsule + the award, not on this store-wide bar.
  function currentBase() {
    return num(window.SHFTD_DEFAULT_MULTIPLIER) || 1;
  }
  // A cart-wide boost code MULTIPLIES the base rate (base × code); the store base
  // and the additive tier bonuses are unchanged. cart-entries-preview.js publishes
  // the active code's multiplier here and fires "shftd:entries-updated" on change,
  // so the bar's faces read base × code + tier add — matching the capsule + award.
  function codeMult() {
    var m = num(window.SHFTD_APPLIED_CODE_MULT);
    return m > 1 ? m : 1;
  }
  // Resolved tier add = fixed increments + base × cumulative %-of-base. So a
  // "+0.5×base" tier reads 12X at base 8 and 6X at base 4 — the ladder rescales
  // with the store base, matching the backend's add_per_dollar.
  function resolvedAdd(m, base) { return base * (m.cumPct || 0) + (m.cumAdd || 0); }
  // Resolved bonus = fixed + base × base-units, the base part rounded UP to a clean
  // 5 (e.g. +3× base at base 8 = 24 → 25). Matches the backend award (rounded per tier).
  function bonusVal(m, base) { var bb = base * (m.bonusBase || 0); return (m.bonus || 0) + (bb > 0 ? Math.ceil(bb / 5) * 5 : 0); }
  function rewardFor(m, base) {
    if (m.isMult) return ((window.shftdLocale&&window.shftdLocale.ctb_reward_mult)||"__RATE__ entries").replace("__RATE__", fmtMult(base * codeMult() + resolvedAdd(m, base)));
    if (m.isBonus) return ((window.shftdLocale&&window.shftdLocale.ctb_reward_bonus)||"+__N__ entries").replace("__N__", bonusVal(m, base));
    return m.reward;
  }
  // Node DOT: bonus rungs show a ticket icon; multiplier rungs show the resolved
  // rate (12X); perk rungs (free shipping) show their icon.
  function nodeDot(m, base) {
    if (m.isBonus) return iconSvg("ticket");
    if (m.isMult) return esc(fmtMult(base * codeMult() + resolvedAdd(m, base)));
    return iconSvg(m.icon);
  }
  // Node LABEL: bonus rungs name the reward ("+24 Entries"); multiplier + perk
  // rungs keep the dollar threshold. Only the start/bonus tiers change.
  function nodeLabel(m, base) {
    if (m.isBonus) return ((window.shftdLocale&&window.shftdLocale.ctb_node_bonus)||"+__N__ Entries").replace("__N__", bonusVal(m, base));
    return money(m.at);
  }

  // ---- Progressive scroll geometry -------------------------------------------
  // Too many rungs to show at once, so the ladder SCROLLS smoothly instead of
  // paging. Positions live on a virtual rail in track-% units: the origin at 0,
  // each rung SLOT apart. `pos` is the live subtotal's continuous spot on that
  // rail; the rail shifts left (railLeft) so the leading edge sits near VX and
  // rungs glide past — early rungs fill normally (railLeft 0), deeper rungs
  // scroll. No windows, no rebuild, no snap.
  var SLOT = 30, VX = 62; // track-%: rung spacing, leading-edge target
  function railGeom(sub, ms) {
    var pos = ms.length * SLOT, prevAt = 0, prevPos = 0;
    for (var i = 0; i < ms.length; i++) {
      var rp = (i + 1) * SLOT;
      if (sub <= ms[i].at) {
        var span = ms[i].at - prevAt;
        pos = prevPos + (span > 0 ? (sub - prevAt) / span : 1) * (rp - prevPos);
        break;
      }
      prevAt = ms[i].at; prevPos = rp;
    }
    var maxPos = ms.length * SLOT;
    // At the far end the last rung settles at ~97% (flush to the bar's end),
    // not floating mid-track. Elsewhere the leading edge sits near VX.
    var railLeft = Math.max(Math.min(0, 97 - maxPos), Math.min(0, VX - pos));
    return { pos: pos, railLeft: railLeft, edge: pos + railLeft, maxPos: maxPos };
  }
  // Viewport-% → opacity: fade rungs at both edges so they enter/leave softly
  // instead of popping (and vanish once fully off-track). Full opacity holds out
  // to 97% so the last rung reads solid at the end.
  function fade(vp) {
    if (vp <= -2 || vp >= 107) return 0;
    if (vp < 8) return (vp + 2) / 10;
    if (vp > 97) return (107 - vp) / 10;
    return 1;
  }

  function containers() {
    return document.querySelectorAll("[data-cart-tiers]");
  }

  // Build the static skeleton (headline shell + track + fill + node faces) into a
  // container ONCE. render() then only mutates the fill width / node classes /
  // headline text, so CSS transitions run smoothly instead of the whole bar
  // being torn down and rebuilt (which made the fill snap). Fill starts at
  // `fillPct` so the build itself never animates from zero.
  function buildSkeleton(root, fillPct, positions) {
    var maxAt = milestones[milestones.length - 1].at || 1;
    var base = currentBase();
    var nodesHtml = milestones
      .map(function (m, i) {
        var p = positions[i];
        return (
          '<div class="ctb__node ctb__r' + m.rarity + '" style="left:' + p.left +
          '%;opacity:' + p.opacity + '" data-key="' + esc(m.key) + '" title="' + esc(money(m.at) + ": " + rewardFor(m, base)) + '">' +
          '<span class="ctb__dot">' + nodeDot(m, base) + "</span>" +
          '<span class="ctb__label">' + esc(nodeLabel(m, base)) + "</span>" +
          "</div>"
        );
      })
      .join("");
    root.innerHTML =
      '<div class="ctb__headline"></div>' +
      '<div class="ctb__track" role="progressbar" aria-valuemin="0" aria-valuemax="' + maxAt + '">' +
      '<div class="ctb__current" hidden></div>' +
      '<div class="ctb__fill" style="width:' + fillPct + '%"></div>' +
      nodesHtml +
      "</div>" +
      '<div class="ctb__levelup" hidden>' +
      '<span class="ctb__lu-base"></span>' +
      '<span class="ctb__lu-add"></span>' +
      '<span class="ctb__lu-burst"></span>' +
      '<span class="ctb__lu-result"></span>' +
      '<span class="ctb__lu-ship"></span>' +
      "</div>";
  }

  // Tier-unlock celebration: the old rate + "+NX" converge, flash, and resolve
  // into the new rate. Re-armed each call (offsetWidth reflow restarts the CSS).
  function playLevelUp(lu, baseTxt, addTxt, resultTxt, isDown, shipTxt) {
    var b = lu.querySelector(".ctb__lu-base");
    var a = lu.querySelector(".ctb__lu-add");
    var res = lu.querySelector(".ctb__lu-result");
    var ship = lu.querySelector(".ctb__lu-ship");
    if (b) b.textContent = baseTxt;
    if (a) a.textContent = addTxt;
    if (res) res.textContent = resultTxt;
    if (ship) ship.textContent = shipTxt || "";
    lu.hidden = false;
    lu.classList.toggle("ctb__levelup--ship", !!shipTxt);
    lu.classList.toggle("ctb__levelup--down", !!isDown);
    lu.classList.remove("ctb__levelup--play");
    void lu.offsetWidth;
    lu.classList.add("ctb__levelup--play");
    clearTimeout(lu._t);
    lu._t = setTimeout(function () {
      lu.classList.remove("ctb__levelup--play");
      lu.hidden = true;
    }, 1850);
  }

  function render(subtotalCents) {
    if (milestones === null) milestones = buildMilestones();
    var roots = containers();
    if (!roots.length) return;

    if (milestones.length === 0) {
      roots.forEach(function (r) { r.hidden = true; r.innerHTML = ""; });
      return;
    }

    var sub = Math.max(0, Math.round(subtotalCents || 0));
    lastSubtotal = sub;

    var base = currentBase(); // cart's base rate, so faces match the capsule
    var cm = codeMult();      // a cart-wide boost code multiplies the base term (base × code)

    // Continuous scroll geometry: the fill's leading edge + every rung's live
    // viewport position (all rungs stay in the DOM; they just glide via CSS
    // transitions). Power tracks OVERALL ladder progress, not the pinned edge, so
    // the bar keeps heating up as you climb even once it's scrolling.
    var g = railGeom(sub, milestones);
    var fillPct = Math.max(0, Math.min(100, g.edge));
    var powerFrac = g.maxPos > 0 ? Math.max(0, Math.min(1, g.pos / g.maxPos)) : 0;
    var positions = milestones.map(function (m, i) {
      var vp = g.railLeft + (i + 1) * SLOT;
      return { left: Math.max(-8, Math.min(108, vp)).toFixed(2), opacity: fade(vp).toFixed(3) };
    });
    var chipVp = g.railLeft; // origin chip rides the rail's start
    var chipPos = { left: Math.max(-8, Math.min(108, chipVp)).toFixed(2), opacity: fade(chipVp).toFixed(3) };

    // First unreached milestone = the carrot.
    var next = null;
    for (var i = 0; i < milestones.length; i++) {
      if (sub < milestones[i].at) { next = milestones[i]; break; }
    }

    var headline, headCls;
    if (!next) {
      headline = "🏆 Every reward unlocked";
      headCls = "ctb__headline ctb__headline--won";
    } else {
      // Proximity + reward, no "Spend". The reward differs per rung so it never
      // reads as a repeated catchphrase; the punch comes from the animation.
      headline = ((window.shftdLocale&&window.shftdLocale.ctb_headline_html)||"<span class=\\\"ctb__amt\\\">__AMT__</span> away from <strong>__REWARD__</strong>")
        .replace("__AMT__", esc(money(next.at - sub)))
        .replace("__REWARD__", esc(rewardFor(next, base)));
      headCls = "ctb__headline";
    }

    // Directional crossings since the last paint: gained (forward) vs lost
    // (backward). Suppressed on the very first paint.
    var gained = Object.create(null), lost = Object.create(null);
    var anyGain = false, anyLose = false;
    // reachedCount drives the entries-banner "exclusivity" escalation; curAdd is
    // the cumulative tier bonus of the highest reached mult tier, so the start
    // chip shows the rate the shopper is earning RIGHT NOW (base + reached add).
    var reachedCount = 0, curAdd = 0;
    milestones.forEach(function (m) {
      var reached = sub >= m.at;
      // Only entry tiers (multiplier/bonus) count toward the banner escalation, so
      // the top "exclusive" level lands on the highest reward tier — free shipping
      // and other perk milestones don't inflate it.
      if (reached) { if (m.isMult || m.isBonus) reachedCount++; if (m.isMult) curAdd = resolvedAdd(m, base); }
      if (primed && reached && !reachedKeys[m.key]) { gained[m.key] = true; anyGain = true; }
      if (primed && !reached && reachedKeys[m.key]) { lost[m.key] = true; anyLose = true; }
      reachedKeys[m.key] = reached;
    });
    primed = true;

    // A forward cross that raises the live multiplier fires the centered combine
    // (old rate + "+NX" → new rate). Multi-tier jumps collapse into one net combine.
    var luPlay = false, luDown = false, luBase = "", luAdd = "", luResult = "";
    if (anyGain && curAdd > prevCurAdd) {
      luPlay = true;
      luBase = fmtMult(base * cm + prevCurAdd);
      luAdd = "+" + fmtMult(curAdd - prevCurAdd);
      luResult = fmtMult(base * cm + curAdd);
    } else if (anyLose && curAdd < prevCurAdd) {
      // A backward cross that LOWERS the rate gets the same combine, marked
      // negative: muted palette + a downward "−NX" that drops the rate.
      luPlay = true;
      luDown = true;
      luBase = fmtMult(base * cm + prevCurAdd);
      luAdd = "−" + fmtMult(prevCurAdd - curAdd);
      luResult = fmtMult(base * cm + curAdd);
    }
    prevCurAdd = curAdd;

    // Hold the ladder's MOVEMENT (fill + scroll) until the level-up finishes, so
    // the combine isn't lost under a sliding bar; then it glides to the new state.
    // A single add that unlocks BOTH a tier and free shipping tags a green
    // "+ Free Shipping" onto the combine.
    var now = Date.now();
    if (luPlay) scrollHoldUntil = now + 1700; // ~level-up duration
    var holding = now < scrollHoldUntil;
    var luShip = luPlay && !luDown && !!gained.freeship;
    if (holding) {
      clearTimeout(scrollDeferTimer);
      scrollDeferTimer = setTimeout(function () { render(lastSubtotal); }, scrollHoldUntil - now + 40);
    }

    roots.forEach(function (r) {
      // Build once (or if the container was replaced by a drawer re-render).
      // Everything after is in place — fill width, rung positions + colours all
      // animate via CSS transitions, so the scroll stays smooth (never rebuilt).
      if (!r.querySelector(".ctb__fill")) buildSkeleton(r, fillPct, positions);
      r.hidden = false;
      // Continuous power (0..1 = overall ladder progress) escalates the bar
      // GRADUALLY, like a charge bar: the CSS heats the fill red→gold + blooms
      // the track aura with it, all the way up regardless of the scroll.
      if (!holding) r.style.setProperty("--ctb-power", powerFrac.toFixed(3));

      var h = r.querySelector(".ctb__headline");
      if (h) { h.className = headCls; if (h.innerHTML !== headline) h.innerHTML = headline; }
      var track = r.querySelector(".ctb__track");
      if (track) track.setAttribute("aria-valuenow", sub);
      var fill = r.querySelector(".ctb__fill");
      if (fill && !holding) fill.style.width = fillPct + "%";

      // Subtle circle at the start of the track showing the STORE DEFAULT rate
      // (e.g. 8X) — the floor the ladder climbs from. Fixed (not the live rate, so
      // it doesn't echo the lit rungs). Hidden when there's no real base (base <= 1).
      var curEl = r.querySelector(".ctb__current");
      if (curEl) {
        // Origin chip (store base rate) rides the rail's start and fades off as
        // the ladder scrolls. Hidden entirely when there's no real base.
        curEl.hidden = base <= 1;
        if (base > 1) {
          if (!holding) {
            curEl.style.left = chipPos.left + "%";
            curEl.style.opacity = chipPos.opacity;
          }
          var curTxt = fmtMult(base * cm);
          if (curEl.textContent !== curTxt) curEl.textContent = curTxt;
        }
      }

      if (luPlay) {
        var lu = r.querySelector(".ctb__levelup");
        if (lu) playLevelUp(lu, luBase, luAdd, luResult, luDown, luShip ? "+ Free Shipping" : "");
      }

      milestones.forEach(function (m, i) {
        var el = r.querySelector('[data-key="' + m.key + '"]');
        if (!el) return;
        if (!holding) {
          el.style.left = positions[i].left + "%";
          el.style.opacity = positions[i].opacity;
        }
        el.classList.toggle("ctb__node--reached", sub >= m.at);
        el.classList.toggle("ctb__node--next", !!next && m.key === next.key);
        // Keep faces in sync with the live base: multiplier rungs update the dot
        // (12X); bonus rungs update the label (+24 Entries), dot stays the ticket.
        if (m.isMult) {
          var dot = el.querySelector(".ctb__dot");
          var f = fmtMult(base * cm + resolvedAdd(m, base));
          if (dot && dot.textContent !== f) dot.textContent = f;
        } else if (m.isBonus) {
          var lab = el.querySelector(".ctb__label");
          var lt = ((window.shftdLocale&&window.shftdLocale.ctb_node_bonus)||"+__N__ Entries").replace("__N__", bonusVal(m, base));
          if (lab && lab.textContent !== lt) lab.textContent = lt;
        }
        // Keep the hover tooltip's reward in sync with the live code + tier too.
        var tip = money(m.at) + ": " + rewardFor(m, base);
        if (el.getAttribute("title") !== tip) el.setAttribute("title", tip);
        if (gained[m.key] || lost[m.key]) {
          el.classList.remove("ctb__node--gain", "ctb__node--lose");
          void el.offsetWidth; // reflow → restart the crossing animation
          el.classList.add(gained[m.key] ? "ctb__node--gain" : "ctb__node--lose");
          (function (node) {
            setTimeout(function () { node.classList.remove("ctb__node--gain", "ctb__node--lose"); }, 870);
          })(el);
        }
      });

      if ((anyGain || anyLose) && fill) {
        fill.classList.remove("ctb__fill--surge", "ctb__fill--drop");
        void fill.offsetWidth;
        fill.classList.add(anyGain ? "ctb__fill--surge" : "ctb__fill--drop");
        (function (f) {
          setTimeout(function () { f.classList.remove("ctb__fill--surge", "ctb__fill--drop"); }, 760);
        })(fill);
      }
    });

    // The entries banner grows richer the more tiers are unlocked. The 3 visual
    // levels map to the PROGRESS RATIO (reached / total entry tiers) so it stays
    // generic as tiers are added/removed: any > 0 = L1, half-way = L2, all = L3.
    var totalEntryTiers = 0;
    for (var ti = 0; ti < milestones.length; ti++) {
      if (milestones[ti].isMult || milestones[ti].isBonus) totalEntryTiers++;
    }
    var bannerLevel = 0;
    if (reachedCount > 0 && totalEntryTiers > 0) {
      var ratio = reachedCount / totalEntryTiers;
      bannerLevel = ratio >= 0.999 ? 3 : ratio >= 0.5 ? 2 : 1;
    }
    var banner = document.querySelector(".drawer__entries-banner");
    if (banner) banner.setAttribute("data-tiers-reached", String(bannerLevel));

    // Free-shipping cue in the footer total: strike "Shipping" once the threshold
    // is crossed (uses the same pre-discount subtotal the bar runs on).
    var freeShipThreshold = num(window.SHFTD_FREE_SHIPPING_THRESHOLD);
    var shipNote = document.querySelector("[data-ship-note]");
    if (shipNote) {
      shipNote.classList.toggle("totals__ship-note--free", freeShipThreshold > 0 && sub >= freeShipThreshold);
    }
  }

  function readSeedSubtotal() {
    var el = document.querySelector("[data-cart-tiers][data-subtotal]");
    return el ? num(el.getAttribute("data-subtotal")) : 0;
  }

  // Optimistic subtotal from the drawer's line rows (pre-discount: unit × qty),
  // for instant bar movement on quantity +/- before /cart.js catches up.
  function domSubtotalCents() {
    var rows = document.querySelectorAll("#CartDrawer .cart-item");
    if (!rows.length) return null;
    var sum = 0;
    for (var i = 0; i < rows.length; i++) {
      var up = parseInt(rows[i].getAttribute("data-unit-price"), 10) || 0;
      var qi = rows[i].querySelector(".quantity__input");
      var q = qi ? parseInt(qi.value, 10) || 0 : 0;
      sum += up * q;
    }
    return sum;
  }

  // Debounced authoritative refresh. MUST outlast the drawer's own qty pipeline
  // (cart-drawer.js debounces /cart/change.js by 400ms, then the network), or
  // /cart.js returns the PRE-change subtotal and clobbers the correct optimistic
  // paint with no retry. 1100ms matches cart-entries-preview.js's qty debounce.
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(refresh, 1100);
  }

  function refresh() {
    if (isFetching) { pending = true; return; }
    isFetching = true;
    pending = false;
    fetch(FETCH, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        // Pre-discount subtotal (original_total_price) so the bar's tier
        // qualification matches the webhook award. items_subtotal_price is after
        // line discounts and would mis-qualify a discounted cart.
        if (cart) render(num(cart.original_total_price != null ? cart.original_total_price : cart.items_subtotal_price));
      })
      .catch(function () {})
      .finally(function () {
        isFetching = false;
        if (pending) refresh();
      });
  }

  function bind() {
    // Paint immediately from the SSR subtotal, then reconcile from /cart.js.
    render(readSeedSubtotal());
    refresh();

    ["cart:update", "cart:refresh", "cart-drawer:opened", "cart-drawer:updated", "cart:item-added", "shftd:entries-updated"].forEach(
      function (ev) { document.addEventListener(ev, refresh); }
    );
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS === "object" && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }

    var drawer = document.getElementById("CartDrawer");
    if (!drawer) return;

    // Quantity +/- and manual edits update the drawer IN PLACE (no section
    // re-render), so the observer below + cart events never fire. Hook the
    // controls directly: paint optimistically from the DOM next frame (once the
    // quantity-input has applied the new value), then reconcile from /cart.js.
    function onQty() {
      // Let quantity-input apply the new value, then paint optimistically from
      // the DOM (the qty inputs are authoritative for the subtotal — unit × qty
      // — so this is correct, not just a guess), and reconcile once settled.
      setTimeout(function () {
        var s = domSubtotalCents();
        if (s != null) render(s);
      }, 80);
      scheduleReconcile();
    }
    drawer.addEventListener("click", function (e) {
      if (e.target.closest('button[name="plus"], button[name="minus"]')) onQty();
    });
    drawer.addEventListener("change", function (e) {
      if (e.target.closest(".quantity__input")) onQty();
    });

    // Add / remove / code-apply RE-RENDER #CartDrawer, replacing our container.
    // Repaint SYNCHRONOUSLY from the re-rendered SSR subtotal so the bar never
    // blinks out (the old code waited on an async fetch → visible flicker), then
    // reconcile. Writing our own innerHTML into the nested [data-cart-tiers]
    // doesn't change #CartDrawer's direct children, so this can't self-trigger.
    if (typeof MutationObserver === "function") {
      new MutationObserver(function () {
        var seed = readSeedSubtotal();
        if (seed != null) render(seed);
        scheduleReconcile();
      }).observe(drawer, { childList: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bind);
  } else {
    bind();
  }
})();
